// LLM generated - Model Claude 4 Sonnet
import { useMemo } from 'react';

import { useContest } from '~/domains/contest/hooks/useContest';
import { useAlertManager } from '~/domains/contest/domains/services/hooks/useAlertManager';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';

/**
 * Hook to manage service fee status including alerts and payment information for a contest
 * @param contestData - Optional contest data to use instead of fetching from useContest
 * @returns Object containing service fee alert state, payment status, and helper booleans
 *
 * Note: This hook always calls useContest() due to React hook rules, but when contestData
 * is provided, it uses that instead. The useContest() call will be cached by React Query
 * so no duplicate network requests occur when used inside ManageContestContainer.
 */
export function useServiceFeeStatus(contestData?: FullContest) {
  const { data: fetchedContest } = useContest();
  const contest = contestData || fetchedContest;

  const alertManager = useAlertManager(contest?.alerts);

  const serviceFeeAlert = useMemo(() => alertManager.getFirstServiceFeeAlert(), [alertManager]);

  // Memoize all derived state to prevent unnecessary recalculations
  const derivedState = useMemo(() => {
    const alertType = serviceFeeAlert?.type || null;
    const hasServiceFeeAlert = Boolean(serviceFeeAlert);
    const isServiceFeeCancelled = alertManager.hasServiceFeeCancelled();

    // Check if service fee has been paid
    // If there are no service fee alerts AND payment_details exists, then it's paid
    const isContestPaid = !serviceFeeAlert && Boolean(contest?.payment_details);

    // Check if payment is allowed - consolidates complex conditional logic
    const isPaymentAllowed = Boolean(
      hasServiceFeeAlert && contest?.id && !isServiceFeeCancelled && !isContestPaid
    );

    return {
      serviceFeeAlert,
      hasServiceFeeAlert,
      isServiceFeeDue: alertManager.hasServiceFeeDue(),
      isServiceFeeOverdue: alertManager.hasServiceFeeOverdue(),
      isServiceFeeCancelled,
      alertType,
      isContestPaid,
      paymentDetails: contest?.payment_details || null,
      isPaymentAllowed,
      // Service fee alert for commissioner display purposes
      serviceFeeAlertForDisplay: serviceFeeAlert,
    };
  }, [serviceFeeAlert, alertManager, contest?.payment_details, contest?.id]);

  return derivedState;
}
